import React from "react";
import PropTypes from "prop-types";
import moment from "moment";

const ListHeaderTableComponent = ({ headers, rows }) => {

  // =========================================================
  // CHECK VALID DATE
  // =========================================================

  const isDate = (value) =>
    value &&
    moment(
      value,
      moment.ISO_8601,
      true
    ).isValid();


  // =========================================================
  // FORMAT CELL
  // =========================================================

  const formatCell = (cell) => {
    if (isDate(cell)) {
      return moment(cell).format("DD-MMM-YYYY");
    }

    return cell ?? "";
  };


  // =========================================================
  // RENDER TABLE ROWS
  // =========================================================

  const renderTableRows = () => {

    const rowsWithHeaders = [];

    headers.forEach((headerRow, headerIndex) => {

      /* =====================================================
         HEADER ROW
      ====================================================== */

      rowsWithHeaders.push(
        <tr
          key={`header-row-${headerIndex}`}
          className="lh-table-header-row"
        >
          {headerRow.map((header, colIndex) => (
            <th
              key={`header-${headerIndex}-${colIndex}`}
              className="lh-table-header-cell"
              scope="col"
            >
              {header}
            </th>
          ))}
        </tr>
      );


      /* =====================================================
         DATA ROW
      ====================================================== */

      rows.forEach((dataRow, rowIndex) => {

        if (rowIndex === headerIndex) {

          rowsWithHeaders.push(
            <tr
              key={`data-row-${headerIndex}-${rowIndex}`}
              className="lh-table-data-row"
            >
              {dataRow.map((cell, colIndex) => (
                <td
                  key={
                    `cell-${headerIndex}-${rowIndex}-${colIndex}`
                  }
                  className="lh-table-data-cell"
                >
                  {formatCell(cell)}
                </td>
              ))}
            </tr>
          );

        }
      });


      /* =====================================================
         BLANK SEPARATOR
      ====================================================== */

      rowsWithHeaders.push(
        <tr
          key={`blank-row-${headerIndex}`}
          className="lh-table-blank-row"
        >
          <td
            colSpan={headerRow.length}
          />
        </tr>
      );

    });

    return rowsWithHeaders;
  };


  // =========================================================
  // COMPONENT
  // =========================================================

  return (
    <>
      <style>
        {`

          /* =====================================================
             OUTER WRAPPER
          ===================================================== */

          .lh-table-wrapper {
            width: 100%;
            max-width: 100%;
            min-width: 0;

            box-sizing: border-box;

            overflow-x: hidden;
            overflow-y: visible;

            border-radius: 10px;
          }


          /* =====================================================
             TABLE
          ===================================================== */

          .lh-table {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;

            table-layout: fixed !important;

            border-collapse: separate !important;
            border-spacing: 0 !important;

            margin: 0 !important;

            background: #ffffff;

            box-sizing: border-box;
          }


          /* =====================================================
             HEADER ROW
          ===================================================== */

          .lh-table-header-row {
            background:
              linear-gradient(
                135deg,
                #0d47a1 0%,
                #1565c0 48%,
                #1976d2 100%
              ) !important;

            color: #ffffff !important;
          }


          /* =====================================================
             HEADER CELL
          ===================================================== */

          .lh-table-header-cell {
            background:
              linear-gradient(
                135deg,
                #0d47a1 0%,
                #1565c0 48%,
                #1976d2 100%
              ) !important;

            color: #ffffff !important;

            padding: 10px 9px !important;

            font-size: 12px !important;

            font-weight: 700 !important;

            text-align: center !important;

            vertical-align: middle !important;

            line-height: 1.3;

            /*
              IMPORTANT:
              Header text wraps instead of expanding
              the table.
            */

            white-space: normal !important;

            overflow-wrap: anywhere !important;

            word-break: break-word !important;

            overflow: hidden;

            border: none !important;

            border-right:
              1px solid rgba(255,255,255,0.15) !important;

            border-bottom:
              2px solid rgba(255,255,255,0.20) !important;

            box-sizing: border-box;
          }


          .lh-table-header-cell:last-child {
            border-right: none !important;
          }


          /* =====================================================
             DATA ROW
          ===================================================== */

          .lh-table-data-row {
            background: #ffffff;

            transition:
              background-color 0.18s ease;
          }


          .lh-table-data-row:hover {
            background: #f4f8fd !important;
          }


          /* =====================================================
             DATA CELL
          ===================================================== */

          .lh-table-data-cell {
            padding: 10px 9px !important;

            font-size: 12px !important;

            color: #263238 !important;

            text-align: center;

            vertical-align: middle !important;

            line-height: 1.35;

            /*
              MAIN FIX:
              Allow every value to wrap.
            */

            white-space: normal !important;

            overflow-wrap: anywhere !important;

            word-break: break-word !important;

            overflow: hidden;

            border: none !important;

            border-right:
              1px solid #e5e9ef !important;

            border-bottom:
              1px solid #e5e9ef !important;

            box-sizing: border-box;
          }


          .lh-table-data-cell:last-child {
            border-right: none !important;
          }


          /* =====================================================
             BLANK ROW
          ===================================================== */

          .lh-table-blank-row td {
            height: 7px !important;

            padding: 0 !important;

            border: none !important;

            background: #ffffff !important;
          }


          /* =====================================================
             TABLET
          ===================================================== */

          @media (max-width: 768px) {

            .lh-table-header-cell {
              padding: 8px 5px !important;

              font-size: 10px !important;

              line-height: 1.2;

              white-space: normal !important;

              overflow-wrap: anywhere !important;

              word-break: break-word !important;
            }


            .lh-table-data-cell {
              padding: 8px 5px !important;

              font-size: 10px !important;

              line-height: 1.25;

              white-space: normal !important;

              overflow-wrap: anywhere !important;

              word-break: break-word !important;
            }


            .lh-table-blank-row td {
              height: 5px !important;
            }
          }


          /* =====================================================
             MOBILE
          ===================================================== */

          @media (max-width: 576px) {

            .lh-table-wrapper {
              width: 100%;
              max-width: 100%;
              min-width: 0;

              overflow-x: hidden;
            }


            .lh-table {
              width: 100% !important;
              max-width: 100% !important;
              min-width: 0 !important;

              table-layout: fixed !important;
            }


            .lh-table-header-cell {
              padding: 7px 3px !important;

              font-size: 9px !important;

              line-height: 1.15;

              white-space: normal !important;

              overflow-wrap: anywhere !important;

              word-break: break-word !important;
            }


            .lh-table-data-cell {
              padding: 7px 3px !important;

              font-size: 9px !important;

              line-height: 1.2;

              white-space: normal !important;

              overflow-wrap: anywhere !important;

              word-break: break-word !important;
            }


            .lh-table-blank-row td {
              height: 4px !important;
            }
          }


          /* =====================================================
             VERY SMALL MOBILE
          ===================================================== */

          @media (max-width: 400px) {

            .lh-table-header-cell {
              padding: 6px 2px !important;

              font-size: 8px !important;
            }


            .lh-table-data-cell {
              padding: 6px 2px !important;

              font-size: 8px !important;
            }
          }

        `}
      </style>


      {/* =======================================================
          TABLE
      ======================================================== */}

      <div className="lh-table-wrapper">

        <table className="lh-table">

          <thead>
            {/*
              The component intentionally keeps the existing
              header/data/blank-row structure.
            */}
          </thead>

          <tbody>
            {renderTableRows()}
          </tbody>

        </table>

      </div>
    </>
  );
};


// =============================================================
// PROP TYPES
// =============================================================

ListHeaderTableComponent.propTypes = {

  headers: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.string
    )
  ).isRequired,

  rows: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.instanceOf(Date),
      ])
    )
  ).isRequired,

};


export default ListHeaderTableComponent;